import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BlogEditor, FileUpload, CoverImageModal } from '../../components/blog';
import { LatteButton, LatteSpinner } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../styles/common/blog-components.css';

const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingCoverImage, setSavingCoverImage] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '', // slug 필드 추가
    content_html: '',
    attachment_file: null,
    attachment_url: null
  });
  const [showCoverImageModal, setShowCoverImageModal] = useState(false);
  const [savedPostId, setSavedPostId] = useState(null);
  const [currentCoverImage, setCurrentCoverImage] = useState('');
  const [modalImages, setModalImages] = useState([]);

  // 관리자 권한 체크
  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  // 기존 포스트 데이터 로드 (수정 모드)
  useEffect(() => {
    if (id) {
      loadPost();
    }
  }, [id]);

  const checkAdminStatus = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      if (data?.role !== 'admin') {
        showError('관리자만 접근 가능합니다');
        navigate('/blog');
      } else {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/blog');
    }
  };

  const loadPost = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setFormData({
        title: data.title || '',
        slug: data.slug || '', // slug 필드 추가
        content_html: data.content_html || '',
        attachment_file: null,
        attachment_url: data.attachment_url || null
      });
      setCurrentCoverImage(data.cover_image_url || '');
      setSavedPostId(id); // 수정 모드에서도 postId 설정
    } catch (error) {
      console.error('Error loading post:', error);
      showError('포스트를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };



  const handleFileUpload = async (file) => {
    try {
      const fileExt = file.name.split('.').pop().toLowerCase();
      
      // 허용된 파일 확장자 체크
      const allowedExtensions = ['pdf', 'docx'];
      if (!allowedExtensions.includes(fileExt)) {
        showError('PDF 또는 DOCX 파일만 업로드할 수 있습니다');
        return null;
      }
      
      // 원본 파일명 그대로 사용
      const filePath = `blog-attachments/${file.name}`;

      const { data, error } = await supabase.storage
        .from('portfolios')
        .upload(filePath, file, {
          upsert: true // 같은 이름의 파일이 있으면 덮어쓰기
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('portfolios')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      showError('파일 업로드에 실패했습니다');
      return null;
    }
  };

  // Google 및 Bing에 sitemap ping 보내기
  const pingSearchEngines = async () => {
    try {
      // Google ping
      fetch('http://www.google.com/ping?sitemap=https://latteis.com/sitemap.xml')
        .then(() => console.log('✅ Google에 sitemap ping 성공'))
        .catch(err => console.error('Google ping 실패:', err));
      
      // Bing ping (선택사항)
      fetch('http://www.bing.com/ping?sitemap=https://latteis.com/sitemap.xml')
        .then(() => console.log('✅ Bing에 sitemap ping 성공'))
        .catch(err => console.error('Bing ping 실패:', err));
        
      // 네이버 웹마스터 ping (한국 대상 서비스라면 추가)
      // fetch('https://apis.naver.com/crawl/nsyndi/v2', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      //   body: 'ping_url=' + encodeURIComponent('https://latteis.com/sitemap.xml')
      // });
    } catch (error) {
      console.error('Search engine ping error:', error);
      // Ping 실패는 컨텐츠 발행에 영향을 주지 않음
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content_html) {
      showError('제목과 내용을 입력해주세요');
      return;
    }

    setSaving(true);

    try {
      // 현재 에디터의 최신 내용 가져오기
      let currentContent = formData.content_html;
      
      // 이미지 업로드가 진행 중이면 대기
      if (isImageUploading) {
        showSuccess('이미지 업로드가 진행 중입니다. 잠시만 기다려주세요...');
        
        // 최대 15초 대기
        let waitTime = 0;
        const maxWaitTime = 15000;
        
        while (isImageUploading && waitTime < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, 500));
          waitTime += 500;
        }
        
        if (isImageUploading) {
          showError('이미지 업로드가 완료되지 않았습니다. 잠시 후 다시 시도해주세요.');
          setSaving(false);
          return;
        }
        
        // 업로드 완료 후 최신 컨텐츠 가져오기
        currentContent = formData.content_html;
      }

      let attachmentUrl = formData.attachment_url;
      
      // 새 파일이 선택되었는지 확인
      if (formData.attachment_file) {
        attachmentUrl = await handleFileUpload(formData.attachment_file);
        if (!attachmentUrl) {
          setSaving(false);
          return;
        }
      }

      // HTML에서 태그 추출
      const extractTags = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        const text = div.textContent || div.innerText || '';
        
        // #으로 시작하는 단어들을 찾아서 태그로 추출
        const tagPattern = /#[가-힣a-zA-Z0-9]+/g;
        const matches = text.match(tagPattern) || [];
        
        // 중복 제거하고 # 제거
        const uniqueTags = [...new Set(matches.map(tag => tag.substring(1).toLowerCase()))];
        return uniqueTags;
      };

      // 요약 생성 (HTML에서 텍스트 추출)
      const div = document.createElement('div');
      div.innerHTML = currentContent;
      const summary = (div.textContent || div.innerText || '').substring(0, 200);

      // 태그 추출
      const tags = extractTags(currentContent);

      // ========== AI를 사용한 Slug 생성 ==========
      let generatedSlug = formData.slug; // 수정 모드에서는 기존 slug 유지
      
      // 새 포스트이거나 slug가 없는 경우에만 생성
      if (!id || !formData.slug) {
        showSuccess('SEO 최적화 URL 생성 중...');
        
        try {
          const response = await fetch('/api/blog/generate-slug', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              title: formData.title,
              content: currentContent // 전체 내용을 보내서 분석
            })
          });

          const slugData = await response.json();
          
          if (slugData.success) {
            generatedSlug = slugData.slug;
            console.log('✅ AI가 생성한 slug:', generatedSlug);
          } else {
            console.warn('Slug 생성 실패, 기본값 사용');
            // 실패 시 제목 기반 간단한 slug 생성
            generatedSlug = formData.title
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-+|-+$/g, '')
              .substring(0, 50); // 최대 50자
          }
        } catch (error) {
          console.error('Slug 생성 오류:', error);
          // 오류 시 제목 기반 간단한 slug 생성
          generatedSlug = formData.title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 50);
        }
      }
      // ========================================

      const postData = {
        title: formData.title,
        slug: generatedSlug, // AI가 생성한 slug 사용
        content_html: currentContent, // 최신 컨텐츠 사용
        content_markdown: '', // React-Quill은 HTML만 제공
        attachment_url: attachmentUrl,
        cover_image_url: '', // 일단 비워두고 저장
        summary: summary,
        tags: tags,
        status: 'published',
        author_id: user.id,
        updated_at: new Date().toISOString()
      };

      let postId;
      
      if (id) {
        // 수정
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', id);

        if (error) throw error;
        postId = id;
        showSuccess('포스트가 수정되었습니다');
        
        // 수정 시에도 검색엔진에 ping (업데이트 알림)
        pingSearchEngines();
      } else {
        // 신규 작성
        const { data, error } = await supabase
          .from('blog_posts')
          .insert([postData])
          .select('id')
          .single();

        if (error) throw error;
        postId = data.id;
        showSuccess('포스트가 작성되었습니다');
        
        // 새 글 작성 시 검색엔진에 ping
        pingSearchEngines();
      }

      // 저장 후 이미지 개수에 따라 커버 이미지 처리
      setSavedPostId(postId);
      
      // 저장된 포스트에서 content_html을 다시 가져와서 이미지 추출
      const { data: savedPost, error: fetchError } = await supabase
        .from('blog_posts')
        .select('content_html, cover_image_url, slug') // slug도 가져오기
        .eq('id', postId)
        .single();
        
      if (!fetchError && savedPost) {
        const images = extractImagesFromContent(savedPost.content_html);
        console.log('Extracted images from saved post:', images);
        const finalSlug = savedPost.slug || generatedSlug; // 저장된 slug 사용
        
        if (images.length === 0) {
          // 이미지가 없으면 바로 블로그 목록으로 이동
          navigate('/blog');
        } else if (images.length === 1) {
          // 이미지가 1개면 자동으로 커버 이미지로 설정
          const { error: updateError } = await supabase
            .from('blog_posts')
            .update({ cover_image_url: images[0] })
            .eq('id', postId);
            
          if (!updateError) {
            showSuccess('커버 이미지가 자동으로 설정되었습니다');
          }
          
          // 블로그 상세 페이지로 이동 (slug 사용)
          if (finalSlug) {
            navigate(`/blog/${finalSlug}`);
          } else {
            navigate(`/blog/${postId}`);
          }
        } else {
          // 이미지가 2개 이상이면 모달 표시
          setModalImages(images);
          setCurrentCoverImage(savedPost.cover_image_url || '');
          setShowCoverImageModal(true);
        }
      } else {
        console.error('Error fetching saved post:', fetchError);
        navigate('/blog');
      }
      
    } catch (error) {
      console.error('Error saving post:', error);
      showError('저장에 실패했습니다');
    } finally {
      setSaving(false);
    }
  };

  // 커버 이미지 저장 함수
  const handleCoverImageSave = async (coverImageUrl) => {
    if (!savedPostId) return;
    
    setSavingCoverImage(true);
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ cover_image_url: coverImageUrl })
        .eq('id', savedPostId);

      if (error) throw error;
      
      showSuccess('커버 이미지가 설정되었습니다');
      setShowCoverImageModal(false);
      
      // 약간의 지연 후 페이지 이동
      setTimeout(() => {
        navigate('/blog');
      }, 100);
    } catch (error) {
      console.error('Error saving cover image:', error);
      showError('커버 이미지 저장에 실패했습니다');
    } finally {
      setSavingCoverImage(false);
    }
  };

  // HTML 컨텐츠에서 이미지 URL 추출
  const extractImagesFromContent = (html) => {
    try {
      const div = document.createElement('div');
      div.innerHTML = html;
      const images = div.querySelectorAll('img');
      
      console.log('All img elements found:', images.length);
      
      const imageUrls = Array.from(images)
        .map(img => img.src)
        .filter(src => {
          // HTTP(S) URL만 포함, base64는 제외
          return src && (src.startsWith('http://') || src.startsWith('https://'));
        });
      
      const uniqueUrls = [...new Set(imageUrls)]; // 중복 제거
      console.log('Final extracted images:', uniqueUrls);
      
      return uniqueUrls;
    } catch (error) {
      console.error('Error extracting images:', error);
      return [];
    }
  };

  if (loading || !isAdmin) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <LatteSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="blog-editor-page">
      <form onSubmit={handleSubmit} className="blog-editor-form" onKeyDown={(e) => {
        // Enter 키로 인한 폼 제출 방지 (텍스트 영역 제외)
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
        }
      }}>
        <h1 style={{ marginBottom: 'var(--spacing-6)' }}>
          {id ? '블로그 수정' : '블로그 작성'}
        </h1>

        <div className="blog-editor-field">
          <label className="blog-editor-label">제목</label>
          <input
            type="text"
            className="blog-editor-input"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="제목을 입력하세요"
            required
          />
        </div>

        <div className="blog-editor-field">
          <label className="blog-editor-label">내용</label>
          <BlogEditor
            value={formData.content_html}
            onChange={(value) => setFormData({ ...formData, content_html: value })}
            onImageUploadStatusChange={setIsImageUploading}
          />
          <div className="blog-editor-tags-preview">
            <p className="tags-hint">팁: 글 내용에 #태그 형식으로 작성하면 자동으로 태그로 인식됩니다 (예: #라떼이즈 #블로그)</p>
            {formData.content_html && (() => {
              const div = document.createElement('div');
              div.innerHTML = formData.content_html;
              const text = div.textContent || div.innerText || '';
              const tagPattern = /#[가-힣a-zA-Z0-9]+/g;
              const matches = text.match(tagPattern) || [];
              const uniqueTags = [...new Set(matches.map(tag => tag.substring(1).toLowerCase()))];
              
              return uniqueTags.length > 0 && (
                <div className="tags-preview">
                  <span className="tags-label">인식된 태그:</span>
                  {uniqueTags.map(tag => (
                    <span key={tag} className="tag-chip">#{tag}</span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="blog-editor-field">
          <label className="blog-editor-label">첨부파일</label>
          <FileUpload
            file={formData.attachment_file}
            onChange={(file) => setFormData({ ...formData, attachment_file: file })}
            onRemove={() => setFormData({ ...formData, attachment_file: null, attachment_url: null })}
          />
          {formData.attachment_url && !formData.attachment_file && (
            <div style={{ 
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: 'var(--border-radius-md)',
              border: '1px solid var(--color-gray-300)'
            }}>
              <p style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-700)',
                marginBottom: 'var(--spacing-2)',
                fontWeight: '500'
              }}>
                기존 첨부파일
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                <svg 
                  style={{ width: '20px', height: '20px', color: 'var(--color-gray-600)' }}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <a 
                  href={formData.attachment_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: 'var(--color-primary-600)',
                    textDecoration: 'none',
                    fontSize: 'var(--font-size-sm)',
                    flex: 1
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {decodeURIComponent(formData.attachment_url.split('/').pop().split('?')[0])}
                </a>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, attachment_url: null })}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-gray-500)',
                    cursor: 'pointer',
                    padding: 'var(--spacing-1)'
                  }}
                  title="첨부파일 삭제"
                >
                  <svg 
                    style={{ width: '16px', height: '16px' }}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p style={{ 
                fontSize: 'var(--font-size-xs)', 
                color: 'var(--color-gray-600)', 
                marginTop: 'var(--spacing-2)' 
              }}>
                새 파일을 업로드하면 기존 파일이 대체됩니다.
              </p>
            </div>
          )}
        </div>

        <div className="blog-editor-actions">
          <LatteButton
            type="button"
            variant="outline"
            onClick={() => navigate('/blog')}
            disabled={saving}
          >
            취소
          </LatteButton>
          <LatteButton
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? <LatteSpinner size="sm" /> : (id ? '수정' : '작성')}
          </LatteButton>
        </div>
      </form>
      
      {/* 커버 이미지 선택 모달 */}
      <CoverImageModal
        isOpen={showCoverImageModal}
        onClose={() => {
          setShowCoverImageModal(false);
          // 모달을 닫을 때도 약간의 지연 후 페이지 이동
          setTimeout(() => {
            navigate('/blog');
          }, 100);
        }}
        images={modalImages}
        currentCoverImage={currentCoverImage}
        onSelectImage={(imageUrl) => console.log('Selected:', imageUrl)}
        onSave={handleCoverImageSave}
        saving={savingCoverImage}
      />
    </div>
  );
};

export default BlogEditorPage;
